/**
 * Notification Center — right-anchored drawer showing alerts.
 * Pattern follows ExecutionMonitorDrawer.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Chip,
  Divider,
  Badge,
  Button,
  Link,
  Alert as MuiAlert,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  Close,
  DoneAll,
  DeleteSweep,
  WaterDrop,
  DevicesOther,
  EventBusy,
  Sensors,
  Warning,
  Error as ErrorIcon,
  Info,
  CheckCircle,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '@/hooks/redux';
import {
  markAllNotificationsAsRead,
  clearAllNotifications,
  markNotificationAsRead,
} from '@/store/slices/uiSlice';
import {
  getAlertHistory,
  getAlertSummary,
  acknowledgeAlert,
  resolveAlert,
  acknowledgeAllAlerts,
  resolveAllAlerts,
} from '@/services/notificationApi';
import type { Notification } from '@/types/common';
import { formatAlertTitle, formatAlertMessage } from '@/utils/alertFormatter';
import AlertDetailsDialog, { type AlertDialogPayload } from './AlertDetailsDialog';

interface Props {
  open: boolean;
  onClose: () => void;
}

type FilterType = 'all' | 'water_stress' | 'device' | 'program' | 'weather';

const SEVERITY_ICON: Record<string, React.ReactNode> = {
  critical: <ErrorIcon color="error" fontSize="small" />,
  warning: <Warning color="warning" fontSize="small" />,
  info: <Info color="info" fontSize="small" />,
};

const TYPE_ICON: Record<string, React.ReactNode> = {
  water_stress: <WaterDrop fontSize="small" />,
  device_offline: <DevicesOther fontSize="small" />,
  low_battery: <DevicesOther fontSize="small" />,
  program_failure: <EventBusy fontSize="small" />,
  missed_execution: <EventBusy fontSize="small" />,
  sensor_staleness: <Sensors fontSize="small" />,
};

const NotificationCenterDrawer: React.FC<Props> = ({ open, onClose }) => {
  const { t, i18n } = useTranslation(['alerts', 'common']);
  const dispatch = useAppDispatch();
  const rtNotifications = useAppSelector((s) => s.ui.notifications);

  const [filter, setFilter] = useState<FilterType>('all');
  const [history, setHistory] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  // Selected alert for the details modal. null = modal closed.
  const [detailsAlert, setDetailsAlert] = useState<AlertDialogPayload | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [histRes, sumRes] = await Promise.all([
        getAlertHistory({ limit: 30, status: 'active' }).catch(() => []),
        getAlertSummary().catch(() => null),
      ]);
      setHistory(Array.isArray(histRes) ? histRes : []);
      setSummary(sumRes);
    } catch {
      // Ignore — alerts may not be available yet
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchData();
  }, [open, fetchData]);

  const matchesFilter = (item: any) => {
    if (filter === 'all') return true;
    const aType = item.alertType || item.alert_type || '';
    if (filter === 'device') return ['device_offline', 'low_battery', 'sensor_staleness'].includes(aType);
    if (filter === 'program') return ['program_failure', 'missed_execution'].includes(aType);
    if (filter === 'water_stress') return aType === 'water_stress';
    if (filter === 'weather') return aType.startsWith('weather');
    return true;
  };

  // Merge real-time + history, dedup by id
  const mergedItems = [
    ...rtNotifications.map((n: Notification) => ({ ...n, _source: 'rt' as const })),
    ...(Array.isArray(history) ? history : []).map((h: any) => ({
      id: `alert_${h.id}`,
      alertId: h.id,
      type: h.severity === 'critical' ? 'error' : h.severity === 'warning' ? 'warning' : 'info',
      title: h.title,
      message: h.message,
      timestamp: h.triggered_at,
      alertType: h.alert_type,
      alert_type: h.alert_type,
      trigger_data: h.trigger_data,
      severity: h.severity,
      sourceType: h.source_type,
      sourceId: h.source_id,
      status: h.status,
      farmName: h.trigger_data?.farm_name,
      link: h.trigger_data?.link,
      _source: 'history' as const,
    })),
  ].filter(matchesFilter);

  // Sort newest first
  mergedItems.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  const handleAcknowledge = async (alertId: number) => {
    try {
      await acknowledgeAlert(alertId);
      fetchData();
    } catch { /* ignore */ }
  };

  const handleResolve = async (alertId: number) => {
    try {
      await resolveAlert(alertId);
      fetchData();
    } catch { /* ignore */ }
  };

  const filters: { key: FilterType; label: string }[] = [
    { key: 'all', label: t('alerts:center.filter.all') },
    { key: 'water_stress', label: t('alerts:center.filter.waterStress') },
    { key: 'device', label: t('alerts:center.filter.devices') },
    { key: 'program', label: t('alerts:center.filter.programs') },
    { key: 'weather', label: t('alerts:center.filter.weather') },
  ];

  const relativeTime = (ts: string) => {
    const diff = Date.now() - new Date(ts).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}
    >
      {/* Header */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">{t('alerts:center.title', 'Notifications')}</Typography>
          {summary?.total_active > 0 && (
            <Badge badgeContent={summary.total_active} color="error" />
          )}
        </Box>
        <Box>
          <IconButton
            size="small"
            onClick={async () => {
              dispatch(markAllNotificationsAsRead());
              try { await acknowledgeAllAlerts(); } catch { /* ignore */ }
              fetchData();
            }}
            title={t('alerts:center.markAllRead')}
          >
            <DoneAll fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={async () => {
              dispatch(clearAllNotifications());
              try { await resolveAllAlerts(); } catch { /* ignore */ }
              fetchData();
            }}
            title={t('alerts:center.clearAll')}
          >
            <DeleteSweep fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </Box>

      <Divider />

      {/* Filter chips */}
      <Box sx={{ px: 2, py: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {filters.map((f) => (
          <Chip
            key={f.key}
            label={f.label}
            size="small"
            variant={filter === f.key ? 'filled' : 'outlined'}
            color={filter === f.key ? 'primary' : 'default'}
            onClick={() => setFilter(f.key)}
          />
        ))}
      </Box>

      <Divider />

      {/* Items */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 1 }}>
        {loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && mergedItems.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CheckCircle sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">{t('alerts:noAlerts', 'No alerts at this time')}</Typography>
          </Box>
        )}

        <Stack spacing={1}>
          {mergedItems.map((item: any) => (
            <MuiAlert
              key={item.id}
              severity={item.type === 'error' ? 'error' : item.type === 'warning' ? 'warning' : 'info'}
              icon={TYPE_ICON[item.alertType] || SEVERITY_ICON[item.severity] || undefined}
              sx={{ '& .MuiAlert-message': { width: '100%' } }}
              action={
                item.alertId && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Button
                      size="small"
                      onClick={() => handleAcknowledge(item.alertId)}
                    >
                      {t('alerts:actions.acknowledge', 'Ack')}
                    </Button>
                    <Button
                      size="small"
                      color="success"
                      onClick={() => handleResolve(item.alertId)}
                    >
                      {t('alerts:actions.resolve', 'Resolve')}
                    </Button>
                  </Box>
                )
              }
              onClose={
                item._source === 'rt'
                  ? () => dispatch(markNotificationAsRead(item.id))
                  : undefined
              }
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {formatAlertTitle(t, item.alertType, item.title, item.trigger_data)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {formatAlertMessage(t, i18n.language, item)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                {item.farmName && (
                  <Chip label={item.farmName} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                )}
                <Typography variant="caption" color="text.disabled">
                  {relativeTime(item.timestamp)}
                </Typography>
                <Link
                  component="button"
                  variant="caption"
                  // Open the details modal instead of navigating away. The
                  // user gets full alert context + can choose to acknowledge,
                  // resolve, or follow the link from the modal itself.
                  onClick={() => setDetailsAlert(item as AlertDialogPayload)}
                  sx={{ cursor: 'pointer' }}
                >
                  {t('alerts:actions.viewDetails', 'View details')}
                </Link>
              </Box>
            </MuiAlert>
          ))}
        </Stack>
      </Box>

      <AlertDetailsDialog
        alert={detailsAlert}
        open={detailsAlert != null}
        onClose={() => setDetailsAlert(null)}
        onAcknowledge={(id) => {
          handleAcknowledge(id);
          setDetailsAlert(null);
        }}
        onResolve={(id) => {
          handleResolve(id);
          setDetailsAlert(null);
        }}
        onNavigate={onClose}
      />
    </Drawer>
  );
};

export default NotificationCenterDrawer;
